/**
 * AI video analysis service.
 *
 * Modes (REACT_APP_AI_ANALYSIS_MODE):
 *   mock   — simulated metadata (default)
 *   gemini — Gemini 2.5 Flash via analyze API (local server or main backend)
 *   live   — alias for gemini
 */

import axios from "axios";
import generateMockMetadata from "./mockMetadata";
import normalizeAnalysisResult, {
  AUTOFILL_CONFIDENCE_THRESHOLD,
} from "./normalizeAnalysisResult";
import buildVideoAnalyzePayload, {
  VideoSourceError,
} from "./gemini/buildAnalyzeRequest";
import geminiResponseToRawMetadata from "./gemini/normalizeResponse";
import {
  startVideoAiAnalysis,
  getVideoAiAnalysisStatus,
} from "../../api/HttpApis";

export { AUTOFILL_CONFIDENCE_THRESHOLD };

export const ANALYSIS_STAGES = [
  { id: "uploading", label: "Uploading", weight: 20 },
  { id: "extracting_audio", label: "Extracting Audio", weight: 25 },
  { id: "analyzing_video", label: "Analyzing Video", weight: 25 },
  { id: "generating_metadata", label: "Generating Metadata", weight: 30 },
];

const ANALYSIS_MODE = (process.env.REACT_APP_AI_ANALYSIS_MODE || "mock").toLowerCase();
const GEMINI_BASE_URL = process.env.REACT_APP_GEMINI_ANALYZE_URL || "";
const USE_GEMINI = ANALYSIS_MODE === "gemini" || ANALYSIS_MODE === "live";

const wait = (ms, signal) =>
  new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(new DOMException("Aborted", "AbortError"));
      return;
    }
    const timer = setTimeout(resolve, ms);
    signal?.addEventListener(
      "abort",
      () => {
        clearTimeout(timer);
        reject(new DOMException("Aborted", "AbortError"));
      },
      { once: true }
    );
  });

const runMockAnalysis = async ({ video, onStage, signal, overrides = {} }) => {
  const latency = overrides.latency ?? 900;
  let cumulative = 0;

  for (const stage of ANALYSIS_STAGES) {
    if (signal?.aborted) throw new DOMException("Aborted", "AbortError");

    onStage?.(stage.id, cumulative);

    if (overrides.failAt === stage.id) {
      await wait(latency / 2, signal);
      throw new Error(
        `AI analysis failed during "${stage.label}". Please try again.`
      );
    }

    const steps = 4;
    for (let i = 1; i <= steps; i++) {
      await wait(latency / steps, signal);
      const pct = cumulative + (stage.weight * i) / steps;
      onStage?.(stage.id, Math.min(99, Math.round(pct)));
    }
    cumulative += stage.weight;
  }

  const raw = generateMockMetadata(video, { variant: overrides.variant });
  onStage?.("complete", 100);
  return normalizeAnalysisResult(raw);
};

const postAnalyzeRequest = async (payload) => {
  if (GEMINI_BASE_URL) {
    const url = `${GEMINI_BASE_URL.replace(/\/$/, "")}/videos/ai-analyze`;
    const config =
      payload.type === "multipart"
        ? { headers: { "Content-Type": "multipart/form-data" } }
        : {};
    const response = await axios.post(url, payload.body, config);
    return response.data?.data ?? response.data;
  }

  const config =
    payload.type === "multipart"
      ? { headers: { "Content-Type": "multipart/form-data" } }
      : {};
  const response = await startVideoAiAnalysis(payload.body, config);
  return response.data?.data ?? response.data;
};

const fetchAnalyzeStatus = async (jobId) => {
  if (GEMINI_BASE_URL) {
    const url = `${GEMINI_BASE_URL.replace(/\/$/, "")}/videos/ai-analyze/${jobId}`;
    const response = await axios.get(url);
    return response.data?.data ?? response.data;
  }

  const response = await getVideoAiAnalysisStatus(jobId);
  return response.data?.data ?? response.data;
};

const pollGeminiJob = async ({ jobId, onStage, signal }) => {
  const pollMs = 1500;

  while (true) {
    if (signal?.aborted) throw new DOMException("Aborted", "AbortError");

    const status = await fetchAnalyzeStatus(jobId);

    if (status?.stage) onStage?.(status.stage, status.progress ?? 0);

    if (status?.status === "failed") {
      throw new Error(status.error || "Gemini analysis failed. Please try again.");
    }

    if (status?.status === "complete") {
      onStage?.("complete", 100);
      return status;
    }

    await wait(pollMs, signal);
  }
};

const shouldFallbackToMock = (err) => {
  if (err instanceof VideoSourceError || err?.name === "VideoSourceError") {
    return false;
  }
  const msg = String(err?.message || "");
  if (/hls|m3u8|unsupported mime|unsupported video/i.test(msg)) return false;
  return true;
};

const runGeminiAnalysis = async ({ video, onStage, signal }) => {
  const payload = buildVideoAnalyzePayload(video);
  if (!payload) {
    throw new Error("No video selected for AI analysis.");
  }

  onStage?.(ANALYSIS_STAGES[0].id, 0);

  const start = await postAnalyzeRequest(payload);
  const jobId = start?.job_id || start?.jobId;

  if (!jobId) {
    throw new Error("AI analyze API did not return a job id.");
  }

  const finished = await pollGeminiJob({ jobId, onStage, signal });

  if (finished.result?.raw) {
    return normalizeAnalysisResult(finished.result.raw);
  }

  const gemini =
    finished.result?.gemini ??
    finished.result ??
    finished.gemini ??
    finished;

  const raw = geminiResponseToRawMetadata(gemini, {
    duration: video?.duration ?? video?.video_duration ?? video?.length,
    ...payload.body,
  });

  return normalizeAnalysisResult(raw);
};

/**
 * Runs an AI analysis job against a selected video.
 */
export const runVideoAnalysis = async ({
  video,
  onStage,
  signal,
  overrides = {},
} = {}) => {
  if (USE_GEMINI && !overrides.forceMock) {
    try {
      return await runGeminiAnalysis({ video, onStage, signal });
    } catch (err) {
      if (err?.name === "AbortError") throw err;
      if (!shouldFallbackToMock(err) || overrides.noFallback) throw err;
      console.warn("[AI] Gemini analysis failed, falling back to mock:", err.message);
    }
  }

  return runMockAnalysis({ video, onStage, signal, overrides });
};

export default runVideoAnalysis;
